import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { addOns } from "@/config/stripePlans";
import WhatsAppConnectionCard from "./WhatsAppConnectionCard";
import TelegramConnectionCard from "./TelegramConnectionCard";
import InstagramConnectionCard from "./InstagramConnectionCard";
import FacebookMessengerConnectionCard from "./FacebookMessengerConnectionCard";
import LineConnectionCard from "./LineConnectionCard";
import BusinessDataSection from "./BusinessDataSection";
import TallyConnectionCard from "./TallyConnectionCard";
import TikTokConnectionCard from "./TikTokConnectionCard";
import StripePaymentsRow from "./StripePaymentsRow";
import { Switch } from "@/components/ui/switch";
import { GOOGLE_REVIEWS_CONNECTOR_ENABLED } from "@/config/featureFlags";

// Booking integrations we list but don't offer yet. Their full connection
// cards (MindbodyConnectionCard, AcuityConnectionCard) stay in the codebase
// for when they launch.
const COMING_SOON_BOOKING_CONNECTORS = [
  {
    name: "Mindbody",
    iconSrc: "/mindbody-icon.svg",
    iconBackground: "bg-[#AAFFD8]/20",
    description: "Sync clients, visits, and sales from Mindbody into Dibs Customer Graph.",
  },
  {
    name: "Acuity Scheduling",
    iconSrc: "/acuity-icon.ico",
    iconBackground: "bg-slate-100",
    description: "Sync appointments, appointment types, and clients from Acuity Scheduling into Dibs Customer Graph.",
  },
];

interface ConnectionsProps {
  onNavigateToSection?: (section: string, extraParams?: Record<string, string>) => void;
  onCloseModal?: () => void;
}

const Connections = ({ onNavigateToSection, onCloseModal }: ConnectionsProps) => {
  const { businessId, hasBookingsAddon } = useAuth();
  const queryClient = useQueryClient();
  const bookingsPrice = addOns.bookings?.price.monthly ?? 20;
  const [bookingsToggleLoading, setBookingsToggleLoading] = useState(false);

  const { data: businessData, isLoading: businessLoading } = useQuery({
    queryKey: ["businessConnections", businessId],
    queryFn: async () => {
      if (!businessId) return null;
      const { data, error } = await supabase
        .from("businesses")
        .select("stripe_connect_id, bookings_enabled")
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!businessId,
  });

  const stripeConnectId = businessData?.stripe_connect_id;
  const bookingsEnabled = businessData?.bookings_enabled !== false;

  const setBookingsOperationalState = async (checked: boolean) => {
    if (!businessId || !hasBookingsAddon) return;
    setBookingsToggleLoading(true);
    try {
      const { data, error } = await supabase.rpc("set_business_bookings_enabled", {
        p_business_id: businessId,
        p_enabled: checked,
      });
      if (error) throw error;
      queryClient.setQueryData(["businessConnections", businessId], (previous: typeof businessData) => ({
        ...previous,
        bookings_enabled: data,
      }));
      queryClient.setQueryData(["bookings-enabled", businessId], data);
      toast.success(checked ? "Dibs Bookings activated" : "Dibs Bookings deactivated");
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update Dibs Bookings");
    } finally {
      setBookingsToggleLoading(false);
    }
  };

  if (businessLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <main className="space-y-8">
      <header className="min-w-0 space-y-1">
        <h1 className="text-xl font-heading font-semibold text-foreground">
          Connectors
        </h1>
        <p className="text-sm text-muted-foreground">
          Connect Dibs to your booking tools, chat channels, business data, and
          forms.
        </p>
      </header>

      {/* Bookings Section */}
      <section aria-labelledby="bookings-connections-heading">
        <div className="mb-2 flex items-center gap-4">
          <h2 id="bookings-connections-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Bookings
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <div>
          {/* Dibs Bookings */}
          <div className="flex items-center justify-between gap-4 py-2.5 last:pb-0">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-red-50 p-2.5">
                <img src="/dibs_assets/red_logo_with_transparent_bg.png" alt="Dibs" className="w-full h-full object-contain" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">Dibs Bookings</span>
                  {!hasBookingsAddon ? (
                    <Badge variant="outline">Add on plan to enable</Badge>
                  ) : !bookingsEnabled ? (
                    <Badge variant="outline">Inactive</Badge>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {hasBookingsAddon
                    ? "Your built-in booking system with storefront, listings, and customer management."
                    : `Subscribe to the Dibs Bookings add-on (S$${bookingsPrice}/mo) to unlock the storefront and bookings workspace.`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!hasBookingsAddon ? (
                <Button
                  size="sm"
                  onClick={() => onNavigateToSection?.("settings", { settings: "billing" })}
                >
                  <Sparkles className="h-4 w-4" />
                  Add Dibs Bookings
                </Button>
              ) : (
                <Switch
                  checked={hasBookingsAddon && bookingsEnabled}
                  disabled={bookingsToggleLoading}
                  onCheckedChange={setBookingsOperationalState}
                />
              )}
            </div>
          </div>
          {hasBookingsAddon && <StripePaymentsRow stripeConnectId={stripeConnectId} />}
          {COMING_SOON_BOOKING_CONNECTORS.map((connector) => (
            <div key={connector.name} className="flex items-center justify-between gap-4 py-2.5 last:pb-0">
              <div className="flex items-center gap-4 min-w-0">
                <div className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${connector.iconBackground}`}>
                  <img src={connector.iconSrc} alt="" className="w-7 h-7 object-contain" />
                </div>
                <div className="space-y-1 min-w-0">
                  <span className="font-semibold text-sm">{connector.name}</span>
                  <p className="text-xs text-muted-foreground">{connector.description}</p>
                </div>
              </div>
              <Badge variant="secondary" className="shrink-0">Coming soon</Badge>
            </div>
          ))}
        </div>
      </section>

      {/* Chat channels Section */}
      <section aria-labelledby="chat-connections-heading">
        <div className="mb-2 flex items-center gap-4">
          <h2 id="chat-connections-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Chat channels
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <div className="divide-y divide-border">
          {/* WhatsApp */}
          <WhatsAppConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
          {/* Telegram */}
          <TelegramConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
          {/* Instagram */}
          <InstagramConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
          {/* Facebook Messenger */}
          <FacebookMessengerConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
          {/* LINE */}
          <LineConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
          {/* TikTok: DMs into the inbox first; Instant Forms live inside the card */}
          <TikTokConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
        </div>
      </section>

      <section aria-labelledby="forms-connections-heading">
        <div className="mb-2 flex items-center gap-4">
          <h2 id="forms-connections-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Forms
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <div className="divide-y divide-border">
          <TallyConnectionCard className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0" />
        </div>
      </section>

      {/* Business Data Section */}
      <BusinessDataSection />

      {/* Reputation & Reviews Section (feature flag) */}
      {GOOGLE_REVIEWS_CONNECTOR_ENABLED && (
        <section aria-labelledby="reviews-connections-heading">
          <div className="mb-2 flex items-center gap-4">
            <h2 id="reviews-connections-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
              Reputation &amp; reviews
            </h2>
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <div className="divide-y divide-border">
            <div className="flex items-center justify-between gap-4 py-2.5 last:pb-0">
              <div className="flex items-center gap-4 min-w-0">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 bg-slate-100">
                  <img src="/google-icon.svg" alt="" className="w-7 h-7 object-contain" />
                </div>
                <div className="space-y-1 min-w-0">
                  <span className="font-semibold text-sm">Google Reviews</span>
                  <p className="text-xs text-muted-foreground">Share your review link with customers and track your Google reputation.</p>
                </div>
              </div>
              <Badge variant="secondary" className="shrink-0">Coming soon</Badge>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Connections;
